import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  orderBy, 
  getDocs,
  Timestamp,
  getDoc
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { TimeEntry } from '../types/task';

const TIME_ENTRIES_COLLECTION = 'timeEntries';

/**
 * Manuális időbejegyzés létrehozása
 */
export async function addManualTimeEntry(
  taskId: string,
  userId: string,
  userName: string,
  organizationId: string,
  startTime: Date,
  endTime: Date,
  description?: string,
  isBillable: boolean = true
): Promise<TimeEntry> {
  const duration = Math.floor((endTime.getTime() - startTime.getTime()) / 1000);

  const timeEntryData = {
    taskId,
    userId,
    userName,
    description: description || '',
    startTime: Timestamp.fromDate(startTime),
    endTime: Timestamp.fromDate(endTime),
    duration,
    isBillable,
    organizationId,
    createdAt: Timestamp.fromDate(new Date()),
    updatedAt: Timestamp.fromDate(new Date())
  };

  const docRef = await addDoc(collection(db, TIME_ENTRIES_COLLECTION), timeEntryData);

  return {
    id: docRef.id,
    taskId,
    userId,
    userName,
    description: description || '',
    startTime,
    endTime,
    duration,
    isBillable,
    createdAt: new Date(),
    updatedAt: new Date()
  };
}

/**
 * Időmérő indítása
 */
export async function startTimer(
  taskId: string,
  userId: string,
  userName: string,
  organizationId: string,
  description?: string
): Promise<TimeEntry> {
  // Ellenőrizzük, hogy nincs-e már futó időmérő ennél a tasknál és usernél
  const runningTimer = await getRunningTimer(taskId, userId, organizationId);
  if (runningTimer) {
    throw new Error('Already have a running timer for this task');
  }

  const now = new Date();
  const timeEntryData = {
    taskId,
    userId,
    userName,
    description: description || '',
    startTime: Timestamp.fromDate(now),
    endTime: null,
    duration: null,
    isBillable: true,
    organizationId,
    createdAt: Timestamp.fromDate(now),
    updatedAt: Timestamp.fromDate(now)
  };

  const docRef = await addDoc(collection(db, TIME_ENTRIES_COLLECTION), timeEntryData);

  return {
    id: docRef.id,
    taskId,
    userId,
    userName,
    description: description || '',
    startTime: now,
    endTime: undefined,
    duration: undefined,
    isBillable: true,
    createdAt: now,
    updatedAt: now
  };
}

/**
 * Időmérő szüneteltetése
 */
export async function pauseTimer(timeEntryId: string): Promise<TimeEntry> {
  const timeEntryRef = doc(db, TIME_ENTRIES_COLLECTION, timeEntryId);
  const timeEntryDoc = await getDoc(timeEntryRef);

  if (!timeEntryDoc.exists()) {
    throw new Error('Time entry not found');
  }

  const now = new Date();
  await updateDoc(timeEntryRef, {
    isPaused: true,
    pausedAt: Timestamp.fromDate(now),
    updatedAt: Timestamp.fromDate(now)
  });

  const timeEntry = timeEntryDoc.data();
  return {
    id: timeEntryId,
    ...timeEntry,
    startTime: timeEntry.startTime.toDate(),
    isPaused: true,
    pausedAt: now,
    updatedAt: now
  } as TimeEntry;
}

/**
 * Időmérő folytatása
 */
export async function resumeTimer(timeEntryId: string): Promise<TimeEntry> {
  const timeEntryRef = doc(db, TIME_ENTRIES_COLLECTION, timeEntryId);
  const timeEntryDoc = await getDoc(timeEntryRef);

  if (!timeEntryDoc.exists()) {
    throw new Error('Time entry not found');
  }

  const timeEntry = timeEntryDoc.data();
  const now = new Date();
  
  // Számoljuk ki a szüneteltetett időt
  const pausedAt = timeEntry.pausedAt?.toDate();
  const additionalPausedDuration = pausedAt 
    ? Math.floor((now.getTime() - pausedAt.getTime()) / 1000)
    : 0;
  
  const totalPausedDuration = (timeEntry.pausedDuration || 0) + additionalPausedDuration;

  await updateDoc(timeEntryRef, {
    isPaused: false,
    pausedAt: null,
    pausedDuration: totalPausedDuration,
    updatedAt: Timestamp.fromDate(now)
  });

  return {
    id: timeEntryId,
    ...timeEntry,
    startTime: timeEntry.startTime.toDate(),
    isPaused: false,
    pausedDuration: totalPausedDuration,
    updatedAt: now
  } as TimeEntry;
}

/**
 * Időmérő leállítása
 */
export async function stopTimer(timeEntryId: string): Promise<TimeEntry> {
  const timeEntryRef = doc(db, TIME_ENTRIES_COLLECTION, timeEntryId);
  const timeEntryDoc = await getDoc(timeEntryRef);

  if (!timeEntryDoc.exists()) {
    throw new Error('Time entry not found');
  }

  const timeEntry = timeEntryDoc.data();
  const now = new Date();
  const startTime = timeEntry.startTime.toDate();
  
  // Számoljuk ki a tényleges időt (kivonva a szüneteltetett időt)
  const totalElapsed = Math.floor((now.getTime() - startTime.getTime()) / 1000);
  const pausedDuration = timeEntry.pausedDuration || 0;
  const duration = totalElapsed - pausedDuration;

  await updateDoc(timeEntryRef, {
    endTime: Timestamp.fromDate(now),
    duration,
    isPaused: false,
    updatedAt: Timestamp.fromDate(now)
  });

  return {
    id: timeEntryId,
    ...timeEntry,
    startTime,
    endTime: now,
    duration,
    updatedAt: now
  } as TimeEntry;
}

/**
 * Futó időmérő lekérése egy taskhoz és userhez
 */
export async function getRunningTimer(
  taskId: string,
  userId: string,
  organizationId: string
): Promise<TimeEntry | null> {
  const q = query(
    collection(db, TIME_ENTRIES_COLLECTION),
    where('taskId', '==', taskId),
    where('userId', '==', userId),
    where('organizationId', '==', organizationId),
    where('endTime', '==', null)
  );

  const snapshot = await getDocs(q);
  
  if (snapshot.empty) {
    return null;
  }

  const doc = snapshot.docs[0];
  const data = doc.data();

  return {
    id: doc.id,
    ...data,
    startTime: data.startTime.toDate(),
    endTime: data.endTime ? data.endTime.toDate() : undefined,
    createdAt: data.createdAt.toDate(),
    updatedAt: data.updatedAt.toDate()
  } as TimeEntry;
}

/**
 * Task összes időbejegyzése
 */
export async function getTaskTimeEntries(
  taskId: string,
  organizationId: string
): Promise<TimeEntry[]> {
  const q = query(
    collection(db, TIME_ENTRIES_COLLECTION),
    where('taskId', '==', taskId),
    where('organizationId', '==', organizationId),
    orderBy('startTime', 'desc')
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      startTime: data.startTime.toDate(),
      endTime: data.endTime ? data.endTime.toDate() : undefined,
      createdAt: data.createdAt.toDate(),
      updatedAt: data.updatedAt.toDate()
    } as TimeEntry;
  });
}

/**
 * User összes időbejegyzése
 */
export async function getUserTimeEntries(
  userId: string,
  organizationId: string,
  startDate?: Date,
  endDate?: Date
): Promise<TimeEntry[]> {
  let q = query(
    collection(db, TIME_ENTRIES_COLLECTION),
    where('userId', '==', userId),
    where('organizationId', '==', organizationId),
    orderBy('startTime', 'desc')
  );

  const snapshot = await getDocs(q);
  let entries = snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      startTime: data.startTime.toDate(),
      endTime: data.endTime ? data.endTime.toDate() : undefined,
      createdAt: data.createdAt.toDate(),
      updatedAt: data.updatedAt.toDate()
    } as TimeEntry;
  });

  // Kliens oldalon szűrjük dátum szerint
  if (startDate) {
    entries = entries.filter(e => e.startTime >= startDate);
  }
  if (endDate) {
    entries = entries.filter(e => e.startTime <= endDate);
  }

  return entries;
}

/**
 * Időbejegyzés frissítése (leírás, billable stb.)
 */
export async function updateTimeEntry(
  timeEntryId: string,
  updates: Partial<Pick<TimeEntry, 'description' | 'isBillable' | 'startTime' | 'endTime'>>
): Promise<void> {
  const timeEntryRef = doc(db, TIME_ENTRIES_COLLECTION, timeEntryId);
  
  const updateData: any = {
    ...updates,
    updatedAt: Timestamp.fromDate(new Date())
  };

  // Ha startTime vagy endTime frissül, újraszámoljuk a duration-t
  if (updates.startTime || updates.endTime) {
    const timeEntryDoc = await getDoc(timeEntryRef);
    if (timeEntryDoc.exists()) {
      const data = timeEntryDoc.data();
      const startTime = updates.startTime || data.startTime.toDate();
      const endTime = updates.endTime || (data.endTime ? data.endTime.toDate() : null);
      
      if (endTime) {
        updateData.duration = Math.floor((endTime.getTime() - startTime.getTime()) / 1000);
      }
    }
  }

  // Timestamp konverzió
  if (updates.startTime) {
    updateData.startTime = Timestamp.fromDate(updates.startTime);
  }
  if (updates.endTime) {
    updateData.endTime = Timestamp.fromDate(updates.endTime);
  }

  await updateDoc(timeEntryRef, updateData);
}

/**
 * Időbejegyzés törlése
 */
export async function deleteTimeEntry(timeEntryId: string): Promise<void> {
  const timeEntryRef = doc(db, TIME_ENTRIES_COLLECTION, timeEntryId);
  await deleteDoc(timeEntryRef);
}

/**
 * Task összes idejének összesítése (órában)
 */
export async function getTotalTaskTime(
  taskId: string,
  organizationId: string
): Promise<number> {
  const entries = await getTaskTimeEntries(taskId, organizationId);
  const totalSeconds = entries.reduce((sum, entry) => {
    return sum + (entry.duration || 0);
  }, 0);
  
  return totalSeconds / 3600; // órában
}

/**
 * Formázott idő megjelenítés (X óra Y perc)
 */
export function formatDurationHumanReadable(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  if (hours === 0 && minutes === 0) {
    return '0 perc';
  }
  
  const parts: string[] = [];
  if (hours > 0) {
    parts.push(`${hours} óra`);
  }
  if (minutes > 0) {
    parts.push(`${minutes} perc`);
  }
  
  return parts.join(' ');
}

/**
 * Formázott idő megjelenítés (HH:MM:SS)
 */
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Futó időmérő elapsed time (másodpercben)
 */
export function getElapsedTime(startTime: Date): number {
  return Math.floor((new Date().getTime() - startTime.getTime()) / 1000);
}
